<template>
  <teleport to="body">
    <Transition name="de-progressor">
      <div
        v-if="show"
        class="de-progressor-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="subtitleId"
      >
        <div
          class="de-progressor-dialog"
          :class="{ 'de-progressor-dialog--success': phase === 'success' }"
          @click.stop
        >
          <div class="de-progressor-shine" aria-hidden="true" />

          <div class="de-progressor-header">
            <div class="de-progressor-header__icon" :class="`de-progressor-header__icon--${phase}`">
              <i :class="headerIcon" aria-hidden="true" />
            </div>
            <div class="de-progressor-header__text">
              <h2 :id="titleId" class="de-progressor-title">{{ title }}</h2>
              <p :id="subtitleId" class="de-progressor-subtitle">{{ subtitle }}</p>
            </div>
            <span class="de-progressor-format-pill">
              <i :class="formatIcon" aria-hidden="true" />
              {{ formatLabel }}
            </span>
          </div>

          <div v-if="phase !== 'success'" class="de-progressor-meter" aria-hidden="true">
            <div class="de-progressor-meter__track">
              <div class="de-progressor-meter__fill" :style="{ width: `${percent}%` }" />
            </div>
            <div class="de-progressor-meter__meta">
              <span class="de-progressor-meter__count">
                <strong>{{ completedCount }}</strong>
                <span>/</span>
                <strong>{{ totalCount }}</strong>
              </span>
              <span class="de-progressor-meter__label">{{ meterLabel }}</span>
              <span class="de-progressor-meter__pct">{{ percent }}%</span>
            </div>
          </div>

          <!-- Success hero -->
          <div v-if="phase === 'success'" class="de-progressor-success-hero">
            <div class="de-progressor-success__burst" aria-hidden="true">
              <span
                v-for="n in 8"
                :key="n"
                class="de-progressor-success__spark"
                :style="{ '--spark-i': n }"
              />
            </div>
            <div class="de-progressor-success__ring">
              <i class="bi bi-check-lg" aria-hidden="true" />
            </div>
            <p class="de-progressor-success__ready">{{ resolvedSuccessReadyText }}</p>
            <p v-if="fileName" class="de-progressor-success__file" :title="fileName">
              <i :class="successFileIcon" aria-hidden="true" />
              {{ fileName }}
            </p>
            <div class="de-progressor-success__stats">
              <div
                v-for="stat in successStatCards"
                :key="stat.label"
                class="de-progressor-success__stat"
              >
                <span class="de-progressor-success__stat-value">{{ stat.value }}</span>
                <span class="de-progressor-success__stat-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>

          <!-- Glossy carousel (progress + success) -->
          <div
            v-if="carouselSlides.length"
            class="de-carousel"
            @mouseenter="pauseAutoplay"
            @mouseleave="resumeAutoplay"
          >
            <button
              type="button"
              class="de-carousel__nav de-carousel__nav--prev"
              :disabled="carouselSlides.length <= 1"
              aria-label="Previous"
              @click="goPrev"
            >
              <i class="bi bi-chevron-left" aria-hidden="true" />
            </button>

            <div class="de-carousel__viewport">
              <div
                class="de-carousel__track"
                :style="{ transform: `translateX(-${slideIndex * 100}%)` }"
              >
                <div
                  v-for="(slide, index) in carouselSlides"
                  :key="slide.key"
                  class="de-carousel__slide"
                  :class="[
                    `de-carousel__slide--${slide.status || 'pending'}`,
                    { 'de-carousel__slide--active': index === slideIndex }
                  ]"
                >
                  <div class="de-carousel__card">
                    <div class="de-carousel__card-glow" aria-hidden="true" />
                    <div class="de-carousel__card-top">
                      <span class="de-carousel__status-icon" aria-hidden="true">
                        <span
                          v-if="slide.status === 'active'"
                          class="de-carousel__spin"
                        />
                        <i v-else-if="slide.status === 'done'" class="bi bi-check-lg" />
                        <i v-else-if="slide.status === 'skipped'" class="bi bi-skip-forward-fill" />
                        <i v-else-if="slide.status === 'error'" class="bi bi-x-lg" />
                        <i v-else class="bi bi-circle" />
                      </span>
                      <span v-if="slide.badge" class="de-carousel__badge">{{ slide.badge }}</span>
                      <span class="de-carousel__pos">
                        {{ index + 1 }} / {{ carouselSlides.length }}
                      </span>
                    </div>
                    <h3 class="de-carousel__title">{{ slide.label }}</h3>
                    <p class="de-carousel__detail">{{ slide.detail }}</p>
                    <div
                      v-if="slide.rows != null"
                      class="de-carousel__rows"
                      :class="{ 'de-carousel__rows--skipped': slide.status === 'skipped' }"
                    >
                      <strong>{{ slide.rows.toLocaleString() }}</strong>
                      <span>{{ slide.status === 'skipped' ? 'no rows' : 'rows' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="de-carousel__nav de-carousel__nav--next"
              :disabled="carouselSlides.length <= 1"
              aria-label="Next"
              @click="goNext"
            >
              <i class="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </div>

          <div v-if="carouselSlides.length > 1" class="de-carousel__dots" role="tablist">
            <button
              v-for="(slide, index) in carouselSlides"
              :key="`dot-${slide.key}`"
              type="button"
              class="de-carousel__dot"
              :class="{
                'de-carousel__dot--active': index === slideIndex,
                'de-carousel__dot--done': slide.status === 'done',
                'de-carousel__dot--skipped': slide.status === 'skipped'
              }"
              :aria-label="`Show ${slide.label}`"
              :aria-selected="index === slideIndex"
              role="tab"
              @click="goTo(index)"
            />
          </div>

          <Transition name="de-progressor-foot">
            <p
              v-if="footMessage && phase === 'error'"
              class="de-progressor-foot"
              :class="footClass"
            >
              {{ footMessage }}
            </p>
          </Transition>

          <div class="de-progressor-actions">
            <button
              type="button"
              class="de-progressor-ok"
              :class="{ 'de-progressor-ok--success': phase === 'success' }"
              :disabled="!okEnabled"
              @click="$emit('ok')"
            >
              {{ okLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </teleport>
</template>

<script>
import './DataExportProgressor.css';

let dataExportProgressorTitleSeq = 0;

const FORMAT_ICONS = {
  xlsx: 'bi bi-file-earmark-excel',
  csv: 'bi bi-filetype-csv',
  pdf: 'bi bi-file-earmark-pdf',
  json: 'bi bi-braces'
};

const FORMAT_LABELS = {
  xlsx: 'Excel',
  csv: 'CSV',
  pdf: 'PDF',
  json: 'JSON'
};

export default {
  name: 'DataExportProgressor',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    steps: {
      type: Array,
      default: () => []
    },
    format: {
      type: String,
      default: 'pdf'
    },
    title: {
      type: String,
      default: 'Exporting data'
    },
    subtitle: {
      type: String,
      default: 'Preparing your file…'
    },
    currentLabel: {
      type: String,
      default: ''
    },
    currentDetail: {
      type: String,
      default: ''
    },
    currentEyebrow: {
      type: String,
      default: 'Currently exporting'
    },
    completedCount: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    },
    phase: {
      type: String,
      default: 'running',
      validator: (v) => ['running', 'success', 'error'].includes(v)
    },
    footMessage: {
      type: String,
      default: ''
    },
    footTone: {
      type: String,
      default: 'neutral',
      validator: (v) => ['neutral', 'success', 'warn', 'error'].includes(v)
    },
    okEnabled: {
      type: Boolean,
      default: false
    },
    okLabel: {
      type: String,
      default: 'OK'
    },
    fileName: {
      type: String,
      default: ''
    },
    summaryItems: {
      type: Array,
      default: () => []
    },
    mode: {
      type: String,
      default: 'export',
      validator: (v) => ['export', 'import'].includes(v)
    },
    successReadyText: {
      type: String,
      default: ''
    },
    successStats: {
      type: Array,
      default: null
    }
  },
  emits: ['ok'],
  data() {
    return {
      titleId: `de-progressor-title-${++dataExportProgressorTitleSeq}`,
      subtitleId: `de-progressor-subtitle-${dataExportProgressorTitleSeq}`,
      slideIndex: 0,
      autoplayTimerId: null,
      autoplayPaused: false
    };
  },
  computed: {
    formatKey() {
      return String(this.format || 'pdf').toLowerCase();
    },
    formatIcon() {
      return FORMAT_ICONS[this.formatKey] || 'bi bi-file-earmark-arrow-down';
    },
    formatLabel() {
      return FORMAT_LABELS[this.formatKey] || this.formatKey.toUpperCase();
    },
    percent() {
      if (!this.totalCount) return this.phase === 'success' ? 100 : 8;
      const raw = Math.round((this.completedCount / this.totalCount) * 100);
      if (this.phase === 'success') return 100;
      if (this.phase === 'running' && raw >= 100) return 96;
      return Math.max(0, Math.min(100, raw));
    },
    meterLabel() {
      if (this.phase === 'error') return 'Stopped';
      if (this.totalCount <= 1) return 'In progress';
      return `${this.completedCount} of ${this.totalCount} done`;
    },
    headerIcon() {
      if (this.phase === 'success') return 'bi bi-check2-circle';
      if (this.phase === 'error') return 'bi bi-exclamation-triangle';
      return 'bi bi-cloud-download';
    },
    footClass() {
      if (this.footTone === 'success') return 'de-progressor-foot--success';
      if (this.footTone === 'error') return 'de-progressor-foot--error';
      if (this.footTone === 'warn') return 'de-progressor-foot--warn';
      return '';
    },
    successItems() {
      const mapItem = (s) => {
        const rows = typeof s.rowCount === 'number'
          ? s.rowCount
          : (typeof s.rows === 'number' ? s.rows : null);
        const skipped = rows === 0;
        return {
          key: s.key,
          label: s.label || s.key,
          rows,
          badge: skipped ? 'Skipped' : 'Done',
          status: skipped ? 'skipped' : 'done',
          detail: skipped
            ? 'No rows found — skipped from export'
            : (typeof rows === 'number'
              ? `${rows.toLocaleString()} row${rows === 1 ? '' : 's'} exported`
              : 'Exported successfully')
        };
      };

      if (this.summaryItems.length) {
        return this.summaryItems.map(mapItem);
      }
      return this.steps
        .filter((s) => {
          if (String(s.key).startsWith('_')) return false;
          if (['prepare', 'gather', 'build', 'download'].includes(s.key)) return false;
          return Boolean(s.isDataset) || s.status === 'done' || s.status === 'skipped';
        })
        .map((s) => {
          if (s.status === 'skipped' || s.rows === 0) {
            return {
              key: s.key,
              label: s.label,
              rows: 0,
              badge: 'Skipped',
              status: 'skipped',
              detail: s.detail || 'No rows found — skipped from export'
            };
          }
          const match = /(\d[\d,]*)\s+row/i.exec(s.detail || '');
          const rows = match ? Number(String(match[1]).replace(/,/g, '')) : null;
          if (rows === 0) {
            return {
              key: s.key,
              label: s.label,
              rows: 0,
              badge: 'Skipped',
              status: 'skipped',
              detail: 'No rows found — skipped from export'
            };
          }
          return {
            key: s.key,
            label: s.label,
            rows: Number.isFinite(rows) ? rows : null,
            badge: s.badge || 'Done',
            status: 'done',
            detail: s.detail || 'Exported successfully'
          };
        });
    },
    exportedDatasetCount() {
      return this.successItems.filter((s) => s.status === 'done').length;
    },
    skippedDatasetCount() {
      return this.successItems.filter((s) => s.status === 'skipped').length;
    },
    successDatasetCount() {
      return this.successItems.length
        || Math.max(1, this.totalCount - (this.steps.some((s) => s.key === '_package') ? 1 : 0));
    },
    totalRows() {
      return this.successItems
        .filter((s) => s.status !== 'skipped')
        .reduce((sum, item) => sum + (item.rows || 0), 0);
    },
    formattedRowTotal() {
      return this.totalRows.toLocaleString();
    },
    resolvedSuccessReadyText() {
      if (this.successReadyText) return this.successReadyText;
      if (this.mode === 'import') return 'Import completed successfully';
      return `Your ${this.formatLabel} file is ready`;
    },
    successFileIcon() {
      return this.mode === 'import' ? 'bi bi-upload' : 'bi bi-download';
    },
    successStatCards() {
      if (Array.isArray(this.successStats) && this.successStats.length) {
        return this.successStats;
      }
      if (this.mode === 'import') {
        return [
          { label: 'Created', value: this.importCreatedCount },
          { label: 'Updated', value: this.importUpdatedCount },
          { label: 'Skipped', value: this.importSkippedCount }
        ];
      }
      return [
        { label: 'Exported', value: this.exportedDatasetCount },
        { label: 'Skipped', value: this.skippedDatasetCount },
        { label: 'Total rows', value: this.formattedRowTotal }
      ];
    },
    importCreatedCount() {
      return this.successItems.reduce((s, i) => s + (i.created || 0), 0).toLocaleString();
    },
    importUpdatedCount() {
      return this.successItems.reduce((s, i) => s + (i.updated || 0), 0).toLocaleString();
    },
    importSkippedCount() {
      return this.successItems.reduce((s, i) => s + (i.skipped || 0), 0).toLocaleString();
    },
    carouselSlides() {
      if (this.phase === 'success') {
        return this.successItems.length
          ? this.successItems
          : this.steps.map((s) => ({
            key: s.key,
            label: s.label,
            detail: s.detail || 'Done',
            badge: s.badge || '',
            status: s.status === 'skipped' ? 'skipped' : 'done',
            rows: s.rows != null ? s.rows : null
          }));
      }
      return this.steps.map((s) => {
        const match = /(\d[\d,]*)\s+row/i.exec(s.detail || '');
        const rows = match ? Number(String(match[1]).replace(/,/g, '')) : null;
        return {
          key: s.key,
          label: s.label,
          detail: s.detail || '',
          badge: s.badge || '',
          status: s.status || 'pending',
          rows: Number.isFinite(rows) ? rows : null
        };
      });
    },
    activeStepIndex() {
      const idx = this.steps.findIndex((s) => s.status === 'active');
      if (idx >= 0) return idx;
      const err = this.steps.findIndex((s) => s.status === 'error');
      if (err >= 0) return err;
      return Math.min(this.completedCount, Math.max(0, this.steps.length - 1));
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.slideIndex = 0;
        this.syncCarousel();
      } else {
        this.stopAutoplay();
      }
    },
    phase() {
      this.slideIndex = 0;
      this.syncCarousel();
    },
    activeStepIndex(idx) {
      if (this.phase === 'running' || this.phase === 'error') {
        this.slideIndex = Math.max(0, Math.min(idx, this.carouselSlides.length - 1));
      }
    },
    carouselSlides(list) {
      if (this.slideIndex >= list.length) {
        this.slideIndex = Math.max(0, list.length - 1);
      }
      this.syncCarousel();
    }
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    syncCarousel() {
      this.stopAutoplay();
      if (this.phase === 'success' && this.carouselSlides.length > 1) {
        this.startAutoplay();
      }
    },
    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimerId = setInterval(() => {
        if (this.autoplayPaused || this.phase !== 'success') return;
        this.goNext(true);
      }, 2800);
    },
    stopAutoplay() {
      if (this.autoplayTimerId) {
        clearInterval(this.autoplayTimerId);
        this.autoplayTimerId = null;
      }
    },
    pauseAutoplay() {
      this.autoplayPaused = true;
    },
    resumeAutoplay() {
      this.autoplayPaused = false;
    },
    goTo(index) {
      if (!this.carouselSlides.length) return;
      this.slideIndex = Math.max(0, Math.min(index, this.carouselSlides.length - 1));
    },
    goPrev() {
      if (this.carouselSlides.length <= 1) return;
      this.slideIndex = (this.slideIndex - 1 + this.carouselSlides.length) % this.carouselSlides.length;
    },
    goNext(loop = true) {
      if (this.carouselSlides.length <= 1) return;
      if (loop) {
        this.slideIndex = (this.slideIndex + 1) % this.carouselSlides.length;
      } else {
        this.slideIndex = Math.min(this.slideIndex + 1, this.carouselSlides.length - 1);
      }
    }
  }
};
</script>
